import React from "react";

import { cn } from "~/lib/cn";

interface ConditionalFieldProps {
  /** Whether the field should be shown */
  show: boolean;
  /** Content to show/hide */
  children: React.ReactNode;
  /** Additional CSS classes */
  className?: string;
  /** Animation duration in milliseconds */
  duration?: number;
  /** Whether to include left padding (common pattern in discovery steps) */
  indent?: boolean;
}

/**
 * Component for conditionally showing/hiding form fields with smooth animation.
 * Replaces the manual CSS transition pattern used throughout discovery steps.
 *
 * @example
 * <ConditionalField show={selectedValues.includes("Other")} indent>
 *   <FormField>
 *     <Textarea placeholder="Please describe..." />
 *   </FormField>
 * </ConditionalField>
 */
export function ConditionalField({
  show,
  children,
  className,
  duration = 700,
  indent = false,
}: ConditionalFieldProps) {
  return (
    <div
      className={cn(
        "max-h-0 overflow-hidden opacity-0 transition-[max-height,opacity]",
        {
          "max-h-fit overflow-visible opacity-100": show,
          "pl-6": indent,
        },
        className,
      )}
      style={{
        transitionDuration: `${duration}ms`,
      }}
    >
      {children}
    </div>
  );
}
